import { Modal, useModals } from 'almanac-ui/Modal'
import Styles from './SocialMediaIntegrationSelectionModal.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { useAccountLimitStatus } from 'hooks/useAccountLimitStatus'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { AVAILABLE_SOCIAL_MEDIA_INTEGRATIONS } from 'components/TacticIntegrationList/utils'
import { ExternalIntegrationEnum, ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import client from 'api/client'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { Icon } from 'blaze-ui/icons/Types'
import { Plus1 } from 'blaze-ui/icons/24'
import { useMemo, useState } from 'react'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ConnectedIntegrationsButton } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/ConnectedIntegrationsButton'

interface SocialMediaIntegrationSelectionModalProps {
  onIntegrationAdded?: (integration: ExternalIntegration) => void
}

interface ProviderRowProps {
  provider: ExternalIntegrationEnum
  icon: Icon
  name: string
  integrations: ExternalIntegration[]
  isAtMaxAccounts: boolean
  onConnect: (popup?: Window) => void
  onConnectedButtonConnect: (integrations: ExternalIntegration[]) => void
}

const ProviderRow = ({
  provider,
  icon: Icon,
  name,
  integrations,
  isAtMaxAccounts,
  onConnect,
  onConnectedButtonConnect,
}: ProviderRowProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const buttonText = isAtMaxAccounts ? 'Add Extra Account for $10/month' : 'Add Account'
  const hasIntegrations = integrations.length > 0

  return (
    <div
      className={Styles.providerRow}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={Styles.providerInfo}>
        <div className={Styles.providerIcon}>
          <Icon size={20} />
        </div>
        <Text>{name}</Text>
      </div>
      {hasIntegrations ? (
        <ConnectedIntegrationsButton
          integrations={integrations}
          onConnect={onConnectedButtonConnect}
          trackingVariant="edit_channels_modal"
        />
      ) : (
        isHovered && (
          <InitiateOauthTrigger provider={provider} popUp onInitiatedOauth={onConnect}>
            <Button frontIcon={Plus1} size="md">
              {buttonText}
            </Button>
          </InitiateOauthTrigger>
        )
      )}
    </div>
  )
}

export const SocialMediaIntegrationSelectionModal = ({
  onIntegrationAdded,
}: SocialMediaIntegrationSelectionModalProps) => {
  const { closeModal } = useModals()
  const { connect } = useConnectAccountPopup()
  const { limitedAccountCount, maxAccounts, isAtOrAboveMaxAccounts } = useAccountLimitStatus()
  const { tierUsageStore } = useRootStore()
  const { currentSubscription } = tierUsageStore
  const { data: integrationsData } = useExternalIntegrations()
  const googleBusinessProfileEnabled = useFlipperFlag('google_business_profile_integration')

  const availableIntegrations = useMemo(() => {
    if (googleBusinessProfileEnabled) return AVAILABLE_SOCIAL_MEDIA_INTEGRATIONS
    return AVAILABLE_SOCIAL_MEDIA_INTEGRATIONS.filter(
      (integration) => integration.integrationType !== ExternalIntegrationEnum.GoogleBusinessProfile,
    )
  }, [googleBusinessProfileEnabled])

  const handleConnect = (popup?: Window) => {
    connect(popup, {
      isAdditionalAccount: true,
      onConnect: (newIntegrations: ExternalIntegration[]) => {
        if (currentSubscription?.status === 'trialing') {
          client.trackEvent('Event::ExtraAccountAddedOnTrial', { variant: 'edit_channels_modal' })
        }

        // Notify parent that a new integration was added
        if (newIntegrations.length > 0) {
          onIntegrationAdded?.(newIntegrations[0])
        }
      },
    })
  }

  // Handler for ConnectedIntegrationsButton which handles OAuth internally
  const handleConnectedButtonConnect = (newIntegrations: ExternalIntegration[]) => {
    // Tracking is already handled by ConnectedIntegrationsButton
    // Just notify parent that a new integration was added
    if (newIntegrations.length > 0) {
      onIntegrationAdded?.(newIntegrations[0])
    }
  }

  const isOnStarterPlan = currentSubscription?.plan === PaymentPlanKey.BlazeAutopilotStarter
  const isOnGrowthPlan = currentSubscription?.plan === PaymentPlanKey.BlazeAutopilotGrowth
  const accountCounter = `${limitedAccountCount}/${Math.max(maxAccounts, limitedAccountCount)}`

  let accountCounterText = null
  if (isOnStarterPlan) {
    accountCounterText = `Starter Plan Accounts`
  } else if (isOnGrowthPlan) {
    accountCounterText = `Growth Plan Accounts`
  }

  const getIntegrationsForProvider = (provider: ExternalIntegrationEnum): ExternalIntegration[] => {
    return integrationsData?.external_integrations.filter((integration) => integration.provider === provider) || []
  }

  return (
    <Modal.Root size="sm" aria-label="Select a platform" onPressOutside={closeModal}>
      <Modal.Header
        title="Select a platform"
        actions={
          accountCounterText ? (
            <div className={Styles.accountCounterContainer}>
              <Text variant="secondary" color="var(--dark-90)">
                <span className={Styles.accountCounter}>{accountCounter}</span>
                {accountCounterText}
              </Text>
            </div>
          ) : undefined
        }
      />
      <Modal.Content withoutFooter>
        <div className={Styles.providersSection}>
          <Heading level={3}>Social Media</Heading>
          <div className={Styles.providersList}>
            {availableIntegrations.map((integration) => (
              <ProviderRow
                key={integration.integrationType}
                provider={integration.integrationType}
                icon={integration.icon as Icon}
                name={integration.name}
                integrations={getIntegrationsForProvider(integration.integrationType)}
                isAtMaxAccounts={isAtOrAboveMaxAccounts}
                onConnect={handleConnect}
                onConnectedButtonConnect={handleConnectedButtonConnect}
              />
            ))}
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  )
}
